'use client';

/**
 * Searchable checkbox multi-select for CRM custom field codes +
 * system fields (`name`, `price`).
 *
 * Renders all available fields (schema + system) in a searchable
 * list, plus any stored `staleCodes` as disabled greyed-out chips
 * with a one-click X so the operator can explicitly remove them
 * instead of having them silently auto-pruned. Stale codes are NOT
 * checkable in the list (they no longer exist in the live schema).
 *
 * Props are intentionally dumb — parent owns the draft state, this
 * component is a controlled input.
 */

import { useMemo, useState } from 'react';
import { Check, X, AlertTriangle, Search, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { CrmFieldSchema } from '@/lib/crm/types';
import { CRM_FIELD_CODES_SOFT_CAP } from '@/lib/ai/tools/crm-opportunity/client-types';
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





async function copyText(text: string, label: string, tUi: ReturnType<typeof useTranslations<"AgentTools.AgentsSharedCrmFieldMultiselect">>) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(tUi("copiedValue0", { value0: String(label) }));
  } catch {
    toast.error(tUi("copyFailed"));
  }
}

interface CrmFieldMultiselectProps {
  /** Live custom field schema from the refresh-schema endpoint. */
  schema: CrmFieldSchema[];
  systemFields: CrmFieldSchema[];
  /** Currently-selected field codes (including any stale ones). */
  value: string[];
  /** Codes that were saved but no longer exist in the live schema. */
  staleCodes?: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function CrmFieldMultiselect({
  schema,
  systemFields,
  value,
  staleCodes = [],
  onChange,
  disabled = false,
}: CrmFieldMultiselectProps) {


  const tUi = useTranslations("AgentTools.AgentsSharedCrmFieldMultiselect")

  const [query, setQuery] = useState('');

  const selected = useMemo(() => new Set(value), [value]);
  const staleSet = useMemo(() => new Set(staleCodes), [staleCodes]);
  // Visible stale chips reflect the CURRENT draft value, not the
  // immutable refresh-schema prop. Clicking X on a stale chip
  // removes it from `value`, and on the next render the filter drops
  // it here — the chip disappears immediately instead of waiting for
  // the next refresh. Stale codes that are still in `value` (because
  // the operator hasn't explicitly removed them) keep rendering.
  const visibleStaleCodes = useMemo(
    () => staleCodes.filter((c) => selected.has(c)),
    [staleCodes, selected]
  );

  // The endpoint returns one provider-aware writable schema, including supported system fields.
  const allItems = useMemo(
    () =>
      [...systemFields, ...schema]
      .filter((f) => f.type !== 'unsupported')
      .map((f) => ({
        code: f.code,
        label: f.label,
        hint: f.system ? 'System field' : describeFieldType(f.type),
        enumLabels:
          f.enumOptions && f.enumOptions.length > 0
            ? f.enumOptions.map((o) => o.label)
            : undefined,
      })),
    [schema, systemFields]
  );
  const items = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.trim().toLowerCase();
    return allItems.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        it.code.toLowerCase().includes(q) ||
        it.enumLabels?.some((opt) => opt.toLowerCase().includes(q))
    );
  }, [allItems, query]);
  const allLiveCodes = useMemo(
    () => [...new Set(allItems.map((item) => item.code))],
    [allItems]
  );

  const selectedCount = value.filter((c) => !staleSet.has(c)).length;
  const aboveSoftCap = selectedCount >= CRM_FIELD_CODES_SOFT_CAP;
  const allLiveFieldsSelected =
    allLiveCodes.length > 0 && allLiveCodes.every((code) => selected.has(code));

  function toggle(code: string) {
    if (disabled) return;
    if (selected.has(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  }

  function removeStale(code: string) {
    if (disabled) return;
    onChange(value.filter((c) => c !== code));
  }

  return (
    <div className="space-y-3">
      {/* Search + counter */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={tUi("searchFields")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
            disabled={disabled}
          />
        </div>
        <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
          {selectedCount} <span><span key={tUi("selected")}>{tUi("selected")}</span></span>
        </div>
      </div>

      {aboveSoftCap && (
        <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-900 dark:text-yellow-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span><TranslationText>{tUi.rich("youHaveValue0FieldsSelectedNarrowerWhitelistsKeep", { value0: String(selectedCount) })}</TranslationText></span>
        </div>
      )}

      {/* Stale chips — rendered separately so operators can remove
          them with a single click instead of auto-pruning. */}
      {visibleStaleCodes.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            <span key={tUi("noLongerInCRMSchema")}>{tUi("noLongerInCRMSchema")}</span></div>
          <div className="flex flex-wrap gap-1.5">
            {visibleStaleCodes.map((code) => (
              <Badge
                key={code}
                variant="outline"
                className="gap-1.5 border-dashed text-muted-foreground line-through"
              >
                {code}
                <button
                  type="button"
                  onClick={() => removeStale(code)}
                  disabled={disabled}
                  aria-label={tUi("removeValue0", { value0: String(code) })}
                  className="ml-1 rounded-sm opacity-70 hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Pickable list */}
      <div className="max-h-64 overflow-y-auto rounded-md border">
        {items.length === 0 ? (
          <div key={query ? tUi("noFieldsMatchYourSearch") : tUi("noFieldsAvailable")} className="px-3 py-6 text-center text-sm text-muted-foreground">
            {query ? tUi("noFieldsMatchYourSearch") : tUi("noFieldsAvailable")}
          </div>
        ) : (
          <ul className="divide-y">
            {items.map((item) => {
              const isSelected = selected.has(item.code) && !staleSet.has(item.code);
              const codeWithLabel = `${item.code} (${item.label})`;
              const copyPayload =
                item.enumLabels && item.enumLabels.length > 0
                  ? `${codeWithLabel}: ${item.enumLabels.join(', ')}`
                  : codeWithLabel;
              return (
                <li
                  key={item.code}
                  className={cn(
                    'px-3 py-2 text-sm transition-colors hover:bg-muted/50',
                    disabled && 'opacity-50'
                  )}
                >
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => toggle(item.code)}
                      disabled={disabled}
                      className={cn(
                        'flex min-w-0 flex-1 items-start gap-3 text-left',
                        disabled && 'cursor-not-allowed'
                      )}
                    >
                      <div
                        className={cn(
                          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input'
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{item.label}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {item.code}
                        </div>
                      </div>
                    </button>

                    <div className="flex shrink-0 items-center gap-1">
                      <Badge variant="secondary" className="text-xs">
                        {item.hint}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => void copyText(copyPayload, item.code, tUi)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label={tUi("copyValue0", { value0: String(item.code) })}
                        title={
                          item.enumLabels && item.enumLabels.length > 0
                            ? tUi("copyCodeLabelAllowedValues")
                            : tUi("copyCodeLabel")
                        }
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {item.enumLabels && item.enumLabels.length > 0 && (
                    <div className="mt-1 ml-7 flex flex-wrap gap-1">
                      {item.enumLabels.slice(0, 8).map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => void copyText(opt, opt, tUi)}
                          className="group inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground hover:bg-muted hover:text-foreground"
                          title={tUi("copyValue02", { value0: String(opt) })}
                        >
                          {opt}
                          <Copy className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
                        </button>
                      ))}
                      {item.enumLabels.length > 8 && (
                        <span className="text-[10px] text-muted-foreground"><TranslationText>{tUi.rich("value0More", { value0: String(item.enumLabels.length - 8) })}</TranslationText></span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Bulk-selection conveniences */}
      {!disabled && allLiveCodes.length > 0 && (
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={allLiveFieldsSelected}
            onClick={() => {
              // Preserve stale codes so the operator can still remove them explicitly.
              onChange([...new Set([...value, ...allLiveCodes])]);
            }}
          >
            <span key={tUi("selectAll")}>{tUi("selectAll")}</span></Button>
          {selectedCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={() => {
                // Preserve stale codes so operator can see + remove them explicitly.
                const staleRemaining = value.filter((c) => staleSet.has(c));
                onChange(staleRemaining);
              }}
            >
              <span key={tUi("clearSelection")}>{tUi("clearSelection")}</span></Button>
          )}
        </div>
      )}
    </div>
  );
}

function describeFieldType(type: CrmFieldSchema['type']): string {
  switch (type) {
    case 'text':
    case 'textarea':
      return 'Text';
    case 'number':
      return 'Number';
    case 'checkbox':
      return 'Yes / No';
    case 'date':
      return 'Date';
    case 'url':
      return 'URL';
    case 'select':
    case 'radio':
      return 'Single choice';
    case 'multiselect':
      return 'Multi choice';
    default:
      return type;
  }
}
